/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';
import Shimmer from '../../../../shared/_TBComponents/Shimmer';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import {
  updateWorkoutInfoName,
  updateNonDateBasedWorkoutInfo,
  setActiveModal,
} from '../../ducks/calendarActions';
import CalendarDropdown from './CalendarDropdown';
import WorkoutColorCircle from './WorkoutColorCircle';
import useWorkoutColorUpdate from '../../hooks/useWorkoutColorUpdate';

const viewMargin = {
  dayView: '1px',
  weekView: '1px',
  monthView: '0px',
};

// Base container - switches between horizontal (dayView) and vertical (weekView) layout
const HeaderContainer = styled('div')`
  display: flex;
  flex-direction: ${(props) => (props.viewType === 'weekView' || props.viewType === 'monthView' ? 'column' : 'row')};
  background: ${({ theme }) => theme.colors.surface};
  padding-top: ${(props) => (props.viewType === 'monthView' ? '4px' : props.viewType === 'weekView' ? '8px' : '0px')};
  padding-bottom: ${(props) => (props.viewType === 'monthView' ? '4px' : props.viewType === 'weekView' ? '8px' : '14px')};
  padding-right: ${(props) => (props.viewType === 'monthView' ? '4px' : props.viewType === 'weekView' ? '8px' : '14px')};
  padding-left: ${(props) => {
    if (props.viewType === 'monthView') return props.isActiveDay ? '4px' : '8px';
    if (props.viewType === 'weekView') return '8px';
    return '14px';
  }};

  margin-bottom: ${(props) => viewMargin[props.viewType]};
  min-height: ${(props) => (props.viewType === 'monthView' ? '40px' : props.viewType === 'weekView' && props.programType === 'free-form-program' ? '64px' : props.viewType === 'weekView' && props.programType === 'calendar' ? '82px' : 'auto')};
  border-bottom: ${(props) => (props.viewType === 'dayView' ? `1px solid ${props.theme.colors.borderLight}` : 'none')};
  width: ${(props) => (props.viewType === 'dayView' ? '100%' : 'auto')};
  border-left: none;
`;

// Main content area (date + expand button for weekView, date + workout name + dropdown for dayView)
const MainContent = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  ${(props) => props.viewType === 'weekView' && props.programType !== 'free-form-program' && `
    margin-bottom: 8px;
  `}
  ${(props) => props.viewType === 'weekView' && props.programType === 'free-form-program' && `
    max-height: 24px;
  `}
`;

// Date display area
const DateDisplay = styled('div')`
  display: flex;
  align-items: center;
  ${(props) => props.viewType === 'dayView' && `
    gap: 25px;
    width: 100%;
    max-width: calc(100% - 35px);
    overflow: hidden;
    /* Top padding moved here from HeaderContainer so the color circle can
       poke above the day number without being clipped by overflow: hidden. */
    padding-top: 14px;
  `}
`;

// Week view date styling
const DayName = styled('div')`
  font-family: 'Nunito Sans';
  display: flex;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => (props.viewType === 'monthView' ? props.theme.colors.textStrong : props.theme.colors.text)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DayNumber = styled('div')`
  font-family: 'Nunito Sans';
  font-size: ${(props) => (props.viewType === 'monthView' ? '14px' : '16px')};
  font-weight: 700;
  /* Today pill: an explicit dark chip with white text in both modes, matching
   * MonthDateLabel. It used to be text-on-surface, which inverted to a light
   * chip in dark mode and left the two views disagreeing. */
  color: ${(props) => (props.isToday ? props.theme.colors.white : (props.viewType === 'monthView' ? props.theme.colors.textStrong : props.theme.isDark ? props.theme.colors.textStrong : props.theme.colors.text))};
  background: ${(props) => (props.isToday ? props.theme.colors.buttonPrimaryBg : 'transparent')};
  border-radius: ${(props) => (props.isToday ? '4px' : '0')};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 5px;
`;

// Month view: full date label, e.g. "Sun, Jun 28". Today gets the pill
// treatment so it stays visually distinct without a separate number badge.
const MonthDateLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  display: inline-flex;
  align-items: center;
  padding: ${(props) => (props.isToday ? '2px 8px' : '2px 0')};
  color: ${(props) => (props.isToday ? props.theme.colors.white : (props.theme.isDark ? props.theme.colors.textStrong : props.theme.colors.text))};
  background: ${(props) => (props.isToday ? props.theme.colors.buttonPrimaryBg : 'transparent')};
  border-radius: ${(props) => (props.isToday ? '4px' : '0')};
`;

// Day view date styling
const DateDivColumn = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

// Anchors the workout-color circle to the upper-right of the day-number column.
const DateColumnWrapper = styled('div')`
  position: relative;
  display: flex;
`;

const ColorCirclePosition = styled('div')`
  position: absolute;
  top: -8px;
  right: -12px;
`;

// Week view: anchors the color circle to the upper-right corner of the day number.
const DayNumberWrapper = styled('div')`
  position: relative;
  display: flex;
`;

const ColorCircleWeekPosition = styled('div')`
  position: absolute;
  top: -4px;
  right: -4px;
`;

const ColorCircleMonthPosition = styled('div')`
  position: absolute;
  top: -5px;
  right: -12px;
`;

// Month view: anchors the color circle to the upper-right corner of the date label.
const MonthDateLabelWrapper = styled('div')`
  position: relative;
  display: inline-flex;
`;

// Free-form week view: places the color circle inline, to the right of the
// "WEEK X - DAY Y" text.
const FreeFormDateRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 10px;
`;

// Expand/collapse button for week view
const ExpandButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceSecondary};
  }
  
  .inLineActionsIcons {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

/** Workout name section (below main content in weekView, part of main content in dayView) */
const WorkoutNameSection = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  ${(props) => props.viewType === 'dayView' && `
    overflow: hidden;
    cursor: pointer;
  `}
`;

const Name = styled('div')`
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: ${(props) => (props.viewType === 'dayView' ? '19px' : '16px')};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  overflow: hidden;
  cursor: pointer;
  /* In week/month view the column sizes to content (fit-content upstream), so a
   * long title would widen the column. width:0 makes this contribute nothing to
   * the column's intrinsic width while min-width:100% keeps it visually filling
   * the column, so the title truncates instead of growing the column. */
  ${(props) => (props.viewType === 'weekView' || props.viewType === 'monthView'
    ? 'width: 0; min-width: 100%;'
    : 'width: 100%;')}
`;

const Input = styled('input')`
  font-family: 'Nunito Sans';
  font-weight: 400;
  font-size: ${(props) => (props.viewType === 'monthView' ? '13px' : '16px')};
  color: ${({ theme }) => theme.colors.text};
  ${(props) => props.programType === 'free-form-program' && 'max-height: 24px;'}
  border: none;
  outline: none;
  width: 100%;
  background: transparent;
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const ShimmerTitle = styled('div')`
  width: 100%;
  max-width: 200px;
  height: 24px;
  border-radius: 4px;
`;

const WeekAndDayShimmerTitle = styled('div')`
  width: 92px;
  height: 18px;
  max-height: 24px;
  border-radius: 4px;
`;

const DropdownContainer = styled('div')`
  flex-shrink: 0;
  min-width: 27px;
  padding-bottom: 3px;
  display: flex;
`;

const DropdownAndCopy = styled('div')`
  display: flex;
`;

const CopyContainer = styled('div')`
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: ${(props) => (props.viewType === 'monthView' ? '5px' : '0px')};

  :hover {
    cursor: pointer;
    background: rgba(119, 167, 255, 0.2);
  }

  &:hover .copyIcon {
    fill: ${(props) => props.theme.colors.hover};
  }
`;

const UnifiedDayHeader = ({
  programType,
  viewType = 'dayView',
  date,
  isExpanded,
  onToggleExpand,
  dayKey,
  showWorkoutName = true,
  isWeekView = false,
  filteredWeekDates,
  filteredProgramWeekDays,
}) => {
  const dispatch = useDispatch();
  const textRef = useRef(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const isTvMode = useSelector((state) => state.calendar.ui.isTvMode);
  const [isEditing, setIsEditing] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [copyHovering, setCopyHovering] = useState(false);

  // For dayView, use selectedDate; for weekView, use passed date
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const { colors } = useTheme();
  const workingDate = date || selectedDate;
  const workingDayNumber = date || selectedProgramDayNumber;

  const endpointFormattedDate = moment(workingDate).format('YYYY-MM-DD');
  // Month view's active day gets a border + padding on the cell itself, so drop the
  // header's own left padding for it to keep content aligned with inactive days.
  const monthSelectionActiveDate = useSelector(
    (state) => state.calendar.data.monthSelectionActiveDate,
  );
  const isActiveDay = viewType === 'monthView'
    && monthSelectionActiveDate === endpointFormattedDate;
  const workoutDetails = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      workingDayNumber
    ]) : useSelector((state) => state.calendar.data.calendarItems[endpointFormattedDate]);

  const { colorOptions, handleUpdateColor, displayColor } = useWorkoutColorUpdate({
    programType,
    workoutDetails,
    effectiveDate: workingDate,
    effectiveDayNumber: workingDayNumber,
  });

  const toolTipId = programType === 'free-form-program' ? `workout-name-${workingDayNumber}` : `workout-name-${endpointFormattedDate}`;
  const copyTooltipId = programType === 'free-form-program' ? `copy-workout-${workingDayNumber}` : `copy-workout-${endpointFormattedDate}`;
  const showCopyButton = !isTvMode && programType !== 'free-form-program';

  const [workoutName, setWorkoutName] = useState('');
  const [workoutValue, setWorkoutValue] = useState(null);
  const isCalendarItemsLoading = programType === 'free-form-program' ? useSelector((state) => state.calendar.ui.isSavedItemsLoading) : useSelector((state) => state.calendar.ui.isCalendarItemsLoading);

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Date formatting
  const today = moment().startOf('day');
  const isToday = moment(workingDate).isSame(today, 'day');
  const dateDay = moment(workingDate).format('D');
  const dateDayOfWeek = moment(workingDate).format('ddd').toUpperCase();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    };

    if (!textRef.current) {
      return undefined;
    }

    // Check immediately without timeout
    checkOverflow();

    // Use ResizeObserver for efficient monitoring
    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(textRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [workoutName, isPrimaryAnimatedSideBarOpen]);

  useEffect(() => {
    if (!workoutDetails?.title || workoutDetails.title.trim() === '') {
      setWorkoutName('Add Workout Name');
    } else {
      setWorkoutName(workoutDetails.title);
    }
  }, [workoutDetails]);

  const handleEditClick = () => {
    if (!isCalendarItemsLoading && showWorkoutName) {
      setIsEditing(true);
    }
  };

  const handleInputChange = (e) => {
    setWorkoutName(e.target.value);
    setWorkoutValue(e.target.value);
  };

  const submitWorkoutName = () => {
    const titleToSubmit = workoutName === 'Add Workout Name' ? '' : workoutName;

    if (programType === 'free-form-program') {
      const submissionObject = {
        color: workoutDetails?.color || '',
        title: titleToSubmit,
      };

      const savedWorkoutId = selectedReduxNonDateBasedProgram.id;
      dispatch(updateNonDateBasedWorkoutInfo(
        currentUser.accountCode,
        savedWorkoutId,
        workingDayNumber,
        submissionObject,
        'name',
      ));
    } else {
      const submissionObject = {
        color: workoutDetails?.color || '',
        date: moment(workingDate).format('YYYY-MM-DD'),
        title: titleToSubmit,
      };
      if (Object.keys(selectedReduxAthlete).length) {
        submissionObject.userId = selectedReduxAthlete.id;
      } else {
        submissionObject.calendarId = selectedCalendar.id;
      }
      dispatch(updateWorkoutInfoName(
        currentUser.accountCode,
        submissionObject,
      ));
    }
    setWorkoutValue(null);
  };

  const handleInputBlur = () => {
    setIsEditing(false);

    const currentTitle = workoutDetails?.title || '';
    const newTitle = workoutName === 'Add Workout Name' ? '' : workoutName;

    if (currentTitle === newTitle) {
      setWorkoutValue(null);
      return;
    }

    submitWorkoutName();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setIsEditing(false);

      const currentTitle = workoutDetails?.title || '';
      const newTitle = workoutName === 'Add Workout Name' ? '' : workoutName;

      if (currentTitle === newTitle) {
        setWorkoutValue(null);
        return;
      }

      submitWorkoutName();
    }
  };

  const renderDateDisplay = () => {
    if (viewType === 'weekView' || viewType === 'monthView') {
      return (
        programType === 'free-form-program' ? (
          isCalendarItemsLoading ? (
            <Shimmer>
              <WeekAndDayShimmerTitle />
            </Shimmer>
          ) : (
            <FreeFormDateRow>
              <Text style={{ whiteSpace: 'nowrap' }} fontSize='12px' fontWeight='700' color={colors.text}>{`WEEK ${workoutDetails?.weekNumber} - DAY ${workoutDetails?.dayInWeek}`}</Text>
              {(viewType === 'weekView' || viewType === 'monthView') && (
                <WorkoutColorCircle
                  colorOptions={colorOptions}
                  currentColor={displayColor}
                  onSelect={handleUpdateColor}
                  size={11}
                  marginTop={-2}
                />
              )}
            </FreeFormDateRow>
          )
        ) : viewType === 'monthView' ? (
          <MonthDateLabelWrapper>
            <MonthDateLabel isToday={isToday}>
              {moment(workingDate).format('ddd, MMM D').toUpperCase()}
            </MonthDateLabel>
            <ColorCircleMonthPosition>
              <WorkoutColorCircle
                colorOptions={colorOptions}
                currentColor={displayColor}
                onSelect={handleUpdateColor}
                size={11}
              />
            </ColorCircleMonthPosition>
          </MonthDateLabelWrapper>
        ) : (
          <>
            <DayName viewType={viewType}>{dateDayOfWeek}</DayName>
            <DayNumberWrapper>
              <DayNumber viewType={viewType} isToday={isToday}>{dateDay}</DayNumber>
              {viewType === 'weekView' && (
                <ColorCircleWeekPosition>
                  <WorkoutColorCircle
                    colorOptions={colorOptions}
                    currentColor={displayColor}
                    onSelect={handleUpdateColor}
                    size={11}
                  />
                </ColorCircleWeekPosition>
              )}
            </DayNumberWrapper>
          </>
        )
      );
    }

    // dayView
    return (
      <DateColumnWrapper>
        <DateDivColumn>
          <Text fontSize='19px' fontWeight={700} color={colors.text}>{dateDay}</Text>
          <Text fontSize='12px' fontWeight={700} color={colors.text}>{dateDayOfWeek}</Text>
        </DateDivColumn>
        <ColorCirclePosition>
          <WorkoutColorCircle
            colorOptions={colorOptions}
            currentColor={displayColor}
            onSelect={handleUpdateColor}
          />
        </ColorCirclePosition>
      </DateColumnWrapper>
    );
  };

  const renderWorkoutName = () => {
    if (!showWorkoutName) return null;

    return (
      <Name onClick={handleEditClick} data-tooltip-id={toolTipId} viewType={viewType} isExpanded={isExpanded}>
        {/* Month view appends data in place (floating "Loading more weeks..."
            indicator handles the loading affordance), so skip the workout-name
            shimmer there. */}
        {isCalendarItemsLoading && viewType !== 'monthView' ? (
          <Shimmer>
            <ShimmerTitle />
          </Shimmer>
        ) : isEditing ? (
          <Input
            viewType={viewType}
            value={workoutName === 'Add Workout Name' ? '' : workoutName}
            placeholder={workoutName === 'Add Workout Name' ? workoutName : ''}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            autoFocus
            programType={programType}
          />
        ) : (
          workoutName === 'Add Workout Name' ? (
            <Text style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} fontSize={viewType === 'monthView' ? '13px' : '16px'} color={colors.textSecondary}>{workoutName}</Text>
          ) : (
            <Text ref={textRef} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} fontSize={viewType === 'monthView' ? '13px' : '16px'} color={colors.text} fontWeight={programType === 'free-form-program' && 700}>{workoutName}</Text>
          )
        )}
        {isOverflowing && (
          <ReactTooltip
            id={toolTipId}
            place='top'
            content={(
              <Text color={colors.white} fontSize='13px'>
                {workoutName}
              </Text>
            )}
            style={{
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: colors.white,
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: Math.min(windowWidth - 40, 800),
              padding: '8px',
              zIndex: 9999,
              textAlign: 'center',
            }}
          />
        )}
      </Name>
    );
  };

  return (
    <HeaderContainer viewType={viewType} programType={programType} isActiveDay={isActiveDay}>
      <MainContent viewType={viewType} programType={programType}>
        <DateDisplay viewType={viewType}>
          {renderDateDisplay()}
          {viewType === 'dayView' && showWorkoutName && (
            <WorkoutNameSection viewType={viewType}>
              {renderWorkoutName()}
            </WorkoutNameSection>
          )}
        </DateDisplay>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          marginRight: viewType === 'monthView' ? '10px' : '0px',
        }}
        >
          {viewType === 'weekView' && onToggleExpand && (
          <ExpandButton onClick={() => onToggleExpand(dayKey)}>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon={isExpanded ? 'collapse' : 'expand2'}
              color={colors.textSecondary}
            />
          </ExpandButton>
          )}
          <DropdownAndCopy>
            <DropdownContainer viewType={viewType}>
              {showCopyButton ? (
                <CopyContainer
                  data-tooltip-id={copyTooltipId}
                  viewType={viewType}
                  onMouseEnter={() => setCopyHovering(true)}
                  onMouseLeave={() => setCopyHovering(false)}
                  onClick={() => {
                    // Calculate week range when in week view
                    let modalData = {
                      currentViewedDate: workingDate,
                    };
                    if (isWeekView) {
                      // Find the clicked date in filteredWeekDates by comparing dates
                      const clickedDate = moment(date);
                      const weekStartDate = filteredWeekDates.find((momentDate) => momentDate.isSame(clickedDate, 'day')) || filteredWeekDates[0];
                      const weekEndDate = filteredWeekDates[filteredWeekDates.length - 1];
                      modalData = {
                        ...modalData,
                        weekStartDate: weekStartDate.toDate(),
                        weekEndDate: weekEndDate.toDate(),
                        isFromWeekView: true,
                      };
                    }

                    // Auto-preselect all items for Program Library mode
                    if (programType === 'free-form-program' && nonDateBasedProgramItems) {
                      const dayData = nonDateBasedProgramItems[workingDayNumber];
                      if (dayData && dayData.exercises && dayData.exercises.length > 0) {
                        const preSelectedItems = {};
                        dayData.exercises.forEach((exercise) => {
                          // Only include items with numeric assignedId (filter out strings)
                          if (exercise.assignedId && typeof exercise.assignedId === 'number') {
                            preSelectedItems[exercise.assignedId] = exercise;
                          }
                        });

                        modalData = {
                          ...modalData,
                          preSelectedItems,
                          skipFirstSlide: true,
                          sourceDayNumber: workingDayNumber,
                          sourceWeekNumber: dayData.weekNumber,
                          sourceDayInWeek: dayData.dayInWeek,
                        };
                      }
                    }

                    dispatch(setActiveModal('copyWorkoutModal', modalData));
                  }}
                >
                  <IcomoonReact
                    iconSet={iconSet}
                    size={15}
                    icon='duplicate'
                    color={copyHovering ? colors.hover : colors.textSecondary}
                  />
                </CopyContainer>
              ) : null}
              <ReactTooltip
                id={copyTooltipId}
                place='top'
                content='Copy Workout'
                style={{
                  backgroundColor: 'rgba(68, 68, 68, 1)',
                  color: colors.white,
                  fontFamily: 'Nunito Sans',
                  fontSize: '13px',
                  zIndex: 1000,
                }}
              />
              <CalendarDropdown
                isWeekView={isWeekView}
                dateOrDay={date}
                filteredWeekDates={filteredWeekDates}
              />
            </DropdownContainer>
          </DropdownAndCopy>
        </div>
      </MainContent>

      {(viewType === 'weekView' || viewType === 'monthView') && showWorkoutName && (
        <WorkoutNameSection viewType={viewType}>
          {programType === 'free-form-program' && !isCalendarItemsLoading && !workoutDetails?.exercises?.length ? (
            <Text fontSize='16px' fontWeight='700'>
              Rest Day
            </Text>
          ) : (
            <>
              {renderWorkoutName()}
            </>
          )}
        </WorkoutNameSection>
      )}
    </HeaderContainer>
  );
};

export default React.memo(UnifiedDayHeader);
